import { useLocation } from "react-router-dom";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import * as Sentry from "@sentry/browser";

import { Lead, useHeyFlowLeadContext } from "../contexts/heyFlowLead";
import { PurchaseRemarks } from "./purchaseForm/PurchaseRemarks";
import { PurchaseReturnType } from "./purchaseForm/PurchaseReturnType";
import { PurchaseUrn } from "./purchaseForm/PurchaseUrn";
import { PurchasePetInfo } from "./purchaseForm/PurchasePetInfo";
import { PurchaseReturnAddress } from "./purchaseForm/PurchaseReturnAddress";
import { PurchaseInvoiceAddress } from "./purchaseForm/PurchaseInvoiceAddress";
import { PurchaseContact } from "./purchaseForm/PurchaseContact";
import { PurchasePickupAddress } from "./purchaseForm/PurchasePickupAddress";
import { usePurchaseContext } from "../contexts/purchase";
import { Button } from "./Button";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { PurchasePayment } from "./purchaseForm/PurchasePayment";
import { usePaymentContext } from "../contexts/payment";
import { FormErrorMessage } from "./FormErrorMessage";
import { useStripeLoadFailed } from "../utils/stripeLoader";

// Wie lange das Zahlungsformular laden darf, bevor der Kunde einen Hinweis bzw.
// eine Fehlermeldung mit Neu-laden-Button sieht statt eines endlosen Spinners.
const PAYMENT_ELEMENT_SLOW_AFTER_MILLISECONDS = 10000;
const PAYMENT_ELEMENT_TIMEOUT_MILLISECONDS = 25000;

interface Props {
  lead: Lead;
  heyflowId: string;
  proceedToCheck: () => void;
}

export function PurchaseDetails({ lead, heyflowId, proceedToCheck }: Props) {
  const { transportType, requiresInscription, allowInscriptionInput } = useHeyFlowLeadContext();

  const { submit, purchaseInformation } = usePurchaseContext();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isPaymentElementReady, setIsPaymentElementReady] = useState<boolean>(false);
  const [hasPaymentElementLoadError, setHasPaymentElementLoadError] = useState<boolean>(false);
  const [isPaymentElementSlow, setIsPaymentElementSlow] = useState<boolean>(false);
  const [hasPaymentElementTimeout, setHasPaymentElementTimeout] = useState<boolean>(false);

  const hasStripeLoadFailed = useStripeLoadFailed();

  const {
    createConfirmationToken,
    message,
    isEditingPaymentMethod,
    setIsEditingPaymentMethod,
    confirmationToken,
  } = usePaymentContext();

  const { state } = useLocation();

  const stripe = useStripe();
  const elements = useElements();

  const contactRef = useRef<HTMLDivElement>(null);
  const invoiceAddressRef = useRef<HTMLDivElement>(null);
  const pickupAddressRef = useRef<HTMLDivElement>(null);
  const petInfoRef = useRef<HTMLDivElement>(null);
  const urnRef = useRef<HTMLDivElement>(null);
  const returnTypeRef = useRef<HTMLDivElement>(null);
  const returnAddressRef = useRef<HTMLDivElement>(null);
  const remarksRef = useRef<HTMLDivElement>(null);
  const paymentRef = useRef<HTMLDivElement>(null);

  const {
    lead: { cremation },
  } = lead;

  useEffect(() => {
    switch (state?.scroll) {
      case "contact":
        contactRef.current?.scrollIntoView({ behavior: "smooth" });
        break;
      case "invoiceaddress":
        invoiceAddressRef.current?.scrollIntoView({ behavior: "smooth" });
        break;
      case "pickupaddress":
        pickupAddressRef.current?.scrollIntoView({ behavior: "smooth" });
        break;
      case "returnaddress":
        returnTypeRef.current?.scrollIntoView({ behavior: "smooth" });
        break;
      case "payment":
        paymentRef.current?.scrollIntoView({ behavior: "smooth" });
        break;
    }
  }, [state?.scroll]);

  useEffect(() => {
    if (state?.payment === "open") {
      setIsEditingPaymentMethod(true);
    }
  }, [setIsEditingPaymentMethod, state?.payment]);

  const onFormSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!stripe || !elements || !purchaseInformation || isSubmitting) {
        return;
      }

      setIsSubmitting(true);

      try {
        const submissionResult = submit(purchaseInformation);

        if (submissionResult !== true) {
          const errors = submissionResult;

          switch (true) {
            case !!errors.email:
              contactRef.current?.scrollIntoView({ behavior: "smooth" });
              break;

            case !!errors.invoiceAddressCountry:
            case !!errors.invoiceAddressFirstName:
            case !!errors.invoiceAddressLastName:
            case !!errors.invoiceAddressCompany:
            case !!errors.invoiceAddressStreet:
            case !!errors.invoiceAddressZipCode:
            case !!errors.invoiceAddressCity:
            case !!errors.invoiceAddressPhoneCountryCode:
            case !!errors.invoiceAddressPhoneNumber:
              invoiceAddressRef.current?.scrollIntoView({ behavior: "smooth" });
              break;

            // pickup address
            case !!errors.hasExtraPickupAddress:
            case !!errors.pickupAddressCountry:
            case !!errors.pickupAddressPracticeName:
            case !!errors.pickupAddressStreet:
            case !!errors.pickupAddressZipCode:
            case !!errors.pickupAddressCity:
            case !!errors.pickupAddressPhoneCountryCode:
            case !!errors.pickupAddressPhoneNumber:
              pickupAddressRef.current?.scrollIntoView({ behavior: "smooth" });
              break;

            // pet
            case !!errors.animalType:
            case !!errors.animalName:
            case !!errors.animalWeight:
            case !!errors.animalStage:
              petInfoRef.current?.scrollIntoView({ behavior: "smooth" });
              break;

            // urn
            case !!errors.inscription:
              urnRef.current?.scrollIntoView({ behavior: "smooth" });
              break;

            // return
            case !!errors.returnType:
              returnTypeRef.current?.scrollIntoView({ behavior: "smooth" });
              break;

            // return address
            case !!errors.hasExtraReturnAddress:
            case !!errors.returnAddressCountry:
            case !!errors.returnAddressFirstName:
            case !!errors.returnAddressLastName:
            case !!errors.returnAddressCompany:
            case !!errors.returnAddressStreet:
            case !!errors.returnAddressZipCode:
            case !!errors.returnAddressCity:
              returnAddressRef.current?.scrollIntoView({ behavior: "smooth" });
              break;

            // remarks
            case !!errors.remarks:
              remarksRef.current?.scrollIntoView({ behavior: "smooth" });
              break;
          }

          return;
        }

        if (!confirmationToken || isEditingPaymentMethod) {
          const confirmationTokenCreated = await createConfirmationToken(stripe, elements, purchaseInformation);

          const hasPaymentFormError = !confirmationTokenCreated;
          if (hasPaymentFormError) {
            paymentRef.current?.scrollIntoView({ behavior: "smooth" });
            return;
          }
        }

        setIsEditingPaymentMethod(false);
        proceedToCheck();
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      stripe,
      elements,
      purchaseInformation,
      isSubmitting,
      submit,
      confirmationToken,
      isEditingPaymentMethod,
      setIsEditingPaymentMethod,
      proceedToCheck,
      createConfirmationToken,
    ]
  );

  const buttonWaitsForPaymentElement = !(confirmationToken && !isEditingPaymentMethod) && !isPaymentElementReady;

  // Ohne Wachhund bleibt der Kunde bei blockiertem oder haengendem Stripe.js
  // dauerhaft auf "Bitte warten" sitzen - ohne Meldung und ohne Sentry-Eintrag.
  useEffect(() => {
    if (!buttonWaitsForPaymentElement) {
      setIsPaymentElementSlow(false);
      setHasPaymentElementTimeout(false);
      return;
    }

    const slowTimer = setTimeout(() => {
      setIsPaymentElementSlow(true);
    }, PAYMENT_ELEMENT_SLOW_AFTER_MILLISECONDS);

    const timeoutTimer = setTimeout(() => {
      setHasPaymentElementTimeout(true);
      Sentry.captureMessage("Payment element was not ready in time");
    }, PAYMENT_ELEMENT_TIMEOUT_MILLISECONDS);

    return () => {
      clearTimeout(slowTimer);
      clearTimeout(timeoutTimer);
    };
  }, [buttonWaitsForPaymentElement]);

  const hasPaymentLoadProblem = hasPaymentElementLoadError || hasStripeLoadFailed || hasPaymentElementTimeout;

  const paymentError = hasPaymentLoadProblem
    ? "Die Zahlungsarten konnten nicht geladen werden. Bitte lade die Seite neu. Falls du einen Werbe- oder Tracking-Blocker nutzt, deaktiviere ihn bitte für diese Seite."
    : message;

  return (
    <form onSubmit={onFormSubmit}>
      <div ref={contactRef} />
      <PurchaseContact />
      <div ref={invoiceAddressRef} />
      <PurchaseInvoiceAddress />
      <div ref={pickupAddressRef} />
      {transportType === "pickUp" && <PurchasePickupAddress />}
      <div ref={petInfoRef} />
      <PurchasePetInfo />
      <div ref={urnRef} />
      {(cremation.type === "Einzelkremierung" || cremation.type === "Premiumkremierung") && (requiresInscription || allowInscriptionInput) && (
        <PurchaseUrn
          heyflowId={heyflowId}
          lead={lead}
          requiresInscription={requiresInscription}
          allowInscriptionInput={allowInscriptionInput}
        />
      )}
      <div ref={returnTypeRef} />
      {(cremation.type === "Einzelkremierung" || cremation.type === "Premiumkremierung") && <PurchaseReturnType />}
      <div ref={returnAddressRef} />
      {(cremation.type === "Einzelkremierung" || cremation.type === "Premiumkremierung") && <PurchaseReturnAddress />}
      <div ref={remarksRef} />
      <PurchaseRemarks />
      <div ref={paymentRef} />
      <PurchasePayment
        isEditingPaymentMethod={isEditingPaymentMethod}
        setIsEditingPaymentMethod={setIsEditingPaymentMethod}
        confirmationToken={confirmationToken}
        onPaymentElementReady={() => setIsPaymentElementReady(true)}
        onPaymentElementLoadError={() => {
          Sentry.captureMessage("Payment element could not be loaded");
          setHasPaymentElementLoadError(true);
        }}
      />
      {buttonWaitsForPaymentElement && isPaymentElementSlow && !hasPaymentLoadProblem && (
        <div className="text-neutral-500 text-sm mt-2">
          Die Zahlungsarten werden geladen – bei langsamer Verbindung dauert das einen Moment.
        </div>
      )}
      <FormErrorMessage message={paymentError} />
      {!hasPaymentLoadProblem ? (
        <Button
          type="submit"
          disabled={!stripe || !elements || isSubmitting || buttonWaitsForPaymentElement}
          inactive={isSubmitting || buttonWaitsForPaymentElement}
          caption={!buttonWaitsForPaymentElement ? "Auftrag überprüfen" : "Bitte warten"}
          fullWidth={true}
          primary={true}
          large={true}
          className="mt-7"
          showSpinner={isSubmitting || buttonWaitsForPaymentElement}
        ></Button>
      ) : (
        <>
          <Button
            type="button"
            caption="Seite neuladen"
            fullWidth={true}
            primary={true}
            large={true}
            className="mt-3"
            onClick={() => window.location.reload()}
          ></Button>
        </>
      )}
    </form>
  );
}
